import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Inbox, Send, Loader2, RefreshCw, Mail, ExternalLink, ChevronRight, Settings2,
  Briefcase, UserCheck, MessageCircle, Search, ArrowLeft, Clock, AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useWorkspaceWindow } from '../../context/WorkspaceWindowContext';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;
const BARK_OPPORTUNITY_URL_RE = /https:\/\/(?:www\.)?bark\.com\/find-work\/[^\s<>"')\]]*/gi;
const SAFE_TEXT_URL_RE = /https?:\/\/[^\s<>"')\]]+/gi;

const DEPARTMENTS = [
  {
    id: 'staffing',
    label: 'Staffing',
    description: 'Care home outreach and CRM replies',
    icon: Briefcase,
    crmLabel: 'Sales CRM',
    crmPath: (thread) =>
      thread.entity_id
        ? `/portal/sales-leads?lead=${thread.entity_id}`
        : `/portal/inbox?department=staffing&contact=${encodeURIComponent(thread.contact_email || '')}`,
  },
  {
    id: 'recruitment',
    label: 'Recruitment',
    description: 'Applicant and carer hiring replies',
    icon: UserCheck,
    crmLabel: 'Recruitment',
    crmPath: (thread) =>
      thread.entity_id
        ? `/portal/recruitment/${thread.entity_id}`
        : '/portal/recruitment',
  },
  {
    id: 'info',
    label: 'General',
    description: 'Website and public enquiries',
    icon: MessageCircle,
    crmLabel: 'Website Enquiries',
    crmPath: () => '/portal/contact-submissions',
  },
];

const STATUS_FILTERS = [
  { id: 'all', label: 'All conversations' },
  { id: 'open', label: 'Open' },
  { id: 'unread', label: 'Unread' },
];

function fmtWhen(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function trimUrlPunctuation(value) {
  return String(value || '').replace(/[.,;:!?]+$/g, '');
}

function hasMalformedPercentEncoding(value) {
  return /%(?![0-9A-Fa-f]{2})/.test(value);
}

function isSafeHttpUrl(value) {
  if (!value || hasMalformedPercentEncoding(value)) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

export function findBarkOpportunityUrl(text) {
  if (!text) return null;

  const matches = String(text).matchAll(BARK_OPPORTUNITY_URL_RE);
  for (const match of matches) {
    const candidate = trimUrlPunctuation(match[0]);
    if (hasMalformedPercentEncoding(candidate)) continue;
    try {
      const parsed = new URL(candidate);
      const hostname = parsed.hostname.toLowerCase();
      if (
        parsed.protocol === 'https:' &&
        (hostname === 'bark.com' || hostname === 'www.bark.com') &&
        parsed.pathname.startsWith('/find-work/')
      ) {
        return parsed.toString();
      }
    } catch {
      /* Ignore malformed links in message text. */
    }
  }

  return null;
}

function departmentConfig(departmentId) {
  return DEPARTMENTS.find((d) => d.id === departmentId) || DEPARTMENTS[0];
}

function departmentLabel(departmentId) {
  return departmentConfig(departmentId).label;
}

function threadDisplayName(thread) {
  return thread?.company_name || thread?.contact_name || thread?.contact_email || 'Unknown sender';
}

function threadDepartment(thread, fallbackDepartment) {
  return thread?.department || thread?.inbox_department || fallbackDepartment || 'staffing';
}

function normalDepartment(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function threadBelongsToDepartment(thread, activeDepartment) {
  const active = normalDepartment(activeDepartment);
  if (!thread || !active) return false;

  const declaredFields = ['department', 'inbox_department'].filter((field) =>
    Object.prototype.hasOwnProperty.call(thread, field) && thread[field] != null
  );
  if (declaredFields.length === 0) return true;

  return declaredFields.every((field) => normalDepartment(thread[field]) === active);
}

function threadStatus(thread) {
  return String(thread?.status || (thread?.closed_at ? 'closed' : 'open')).toLowerCase();
}

function threadHasBarkOpportunity(thread) {
  return Boolean(findBarkOpportunityUrl(`${thread?.subject || ''}\n${thread?.last_preview || ''}\n${thread?.preview || ''}`));
}

function countUnread(threads) {
  return threads.filter((thread) => (thread.unread_count || 0) > 0).length;
}

function countOpen(threads) {
  return threads.filter((thread) => threadStatus(thread) === 'open').length;
}

function filterThreads(threads, { searchTerm, mailboxFilter, department }) {
  const query = searchTerm.trim().toLowerCase();
  return threads
    .filter((thread) => {
      if (mailboxFilter === 'unread' && !(thread.unread_count > 0)) return false;
      if (mailboxFilter === 'open' && threadStatus(thread) !== 'open') return false;
      if (!threadBelongsToDepartment(thread, department)) return false;
      if (!query) return true;

      const haystack = [
        threadDisplayName(thread),
        thread.contact_email,
        thread.subject,
        thread.last_preview,
        thread.preview,
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    })
    .sort((a, b) => new Date(b.last_message_at || b.updated_at || 0) - new Date(a.last_message_at || a.updated_at || 0));
}

function splitQuotedHistory(body) {
  const text = String(body || '');
  if (!text.trim()) return { visible: '', quoted: '' };

  const lines = text.split(/\r?\n/);
  const markerIndex = lines.findIndex((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    if (/^On .+wrote:$/i.test(trimmed)) return true;
    if (/^-{2,}\s*Forwarded message\s*-{2,}$/i.test(trimmed)) return true;
    if (/^>{1,}\s?/.test(trimmed)) return true;
    if (
      /^From:\s.+/i.test(trimmed) &&
      /^Sent:\s.+/i.test((lines[index + 1] || '').trim()) &&
      /^To:\s.+/i.test((lines[index + 2] || '').trim()) &&
      /^Subject:\s.+/i.test((lines[index + 3] || '').trim())
    ) {
      return true;
    }
    return false;
  });

  if (markerIndex <= 0) return { visible: text, quoted: '' };

  const visible = lines.slice(0, markerIndex).join('\n').trimEnd();
  const quoted = lines.slice(markerIndex).join('\n').trim();
  if (!visible || !quoted) return { visible: text, quoted: '' };
  return { visible, quoted };
}

function SafeText({ text }) {
  const value = String(text || '');
  const parts = [];
  let lastIndex = 0;

  for (const match of value.matchAll(SAFE_TEXT_URL_RE)) {
    const raw = match[0];
    const start = match.index || 0;
    const safeUrl = trimUrlPunctuation(raw);

    if (start > lastIndex) {
      parts.push(value.slice(lastIndex, start));
    }

    if (isSafeHttpUrl(safeUrl)) {
      const trailing = raw.slice(safeUrl.length);
      parts.push(
        <a
          key={`${safeUrl}-${start}`}
          href={safeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-primary underline underline-offset-2 break-all"
        >
          {safeUrl}
        </a>,
      );
      if (trailing) parts.push(trailing);
    } else {
      parts.push(raw);
    }

    lastIndex = start + raw.length;
  }

  if (lastIndex < value.length) {
    parts.push(value.slice(lastIndex));
  }

  return <>{parts}</>;
}

function MessageBody({ body }) {
  const [showQuoted, setShowQuoted] = useState(false);
  const { visible, quoted } = splitQuotedHistory(body);

  return (
    <div className="space-y-3">
      <p className="whitespace-pre-wrap break-words leading-6">
        <SafeText text={visible} />
      </p>
      {quoted && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3">
          <button
            type="button"
            onClick={() => setShowQuoted((value) => !value)}
            className="text-xs font-semibold text-primary hover:underline"
          >
            {showQuoted ? 'Hide quoted history' : 'Show quoted history'}
          </button>
          {showQuoted && (
            <p className="mt-3 whitespace-pre-wrap break-words text-xs leading-5 text-slate-600">
              <SafeText text={quoted} />
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Badge({ children, tone = 'slate' }) {
  const tones = {
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    red: 'border-red-200 bg-red-50 text-red-700',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
}

function MailboxSidebar({
  department,
  mailboxFilter,
  threads,
  listLoading,
  onDepartmentChange,
  onMailboxFilterChange,
  onRefresh,
  onSetup,
}) {
  const statusCounts = {
    all: threads.length,
    open: countOpen(threads),
    unread: countUnread(threads),
  };

  return (
    <aside className="rounded-lg border border-slate-200 bg-white shadow-sm lg:h-full lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain" aria-label="Mailbox navigation">
      <div className="border-b border-slate-200 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mailbox</p>
            <h2 className="mt-1 text-base font-semibold text-slate-950">Inbox</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onRefresh} disabled={listLoading} aria-label="Refresh inbox">
            <RefreshCw className={`h-4 w-4 ${listLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="space-y-5 p-3">
        <div>
          <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Views</p>
          <div className="space-y-1">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                onClick={() => onMailboxFilterChange(filter.id)}
                className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  mailboxFilter === filter.id
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <span>{filter.label}</span>
                <span className={mailboxFilter === filter.id ? 'text-white/90' : 'text-slate-500'}>
                  {statusCounts[filter.id] ?? 0}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Departments</p>
          <div className="space-y-1">
            {DEPARTMENTS.map((item) => {
              const Icon = item.icon;
              const active = department === item.id;
              const count = active
                ? threads.filter((thread) => threadBelongsToDepartment(thread, item.id)).length
                : null;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onDepartmentChange(item.id)}
                  className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                    active
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </span>
                  {count !== null && <span className="text-white/80">{count}</span>}
                </button>
              );
            })}
          </div>
        </div>

        <Button variant="outline" size="sm" onClick={onSetup} className="w-full justify-start">
          <Settings2 className="h-4 w-4 mr-2" />
          Setup
        </Button>
      </div>
    </aside>
  );
}

function CompactInboxControls({
  department,
  mailboxFilter,
  threads,
  listLoading,
  onDepartmentChange,
  onMailboxFilterChange,
}) {
  const statusCounts = {
    all: threads.length,
    open: countOpen(threads),
    unread: countUnread(threads),
  };

  return (
    <div className="space-y-3 border-b border-slate-200 bg-white p-4 lg:hidden" aria-label="Compact inbox controls">
      <div className="flex gap-1 overflow-x-auto" aria-label="Mailbox views">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.id}
            type="button"
            onClick={() => onMailboxFilterChange(filter.id)}
            disabled={listLoading}
            className={`shrink-0 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
              mailboxFilter === filter.id
                ? 'border-primary bg-primary text-white'
                : 'border-slate-200 bg-white text-slate-700'
            }`}
          >
            {filter.label} {statusCounts[filter.id] ?? 0}
          </button>
        ))}
      </div>
      <div className="flex gap-1 overflow-x-auto" aria-label="Inbox departments">
        {DEPARTMENTS.map((item) => {
          const active = department === item.id;
          const count = active
            ? threads.filter((thread) => threadBelongsToDepartment(thread, item.id)).length
            : null;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onDepartmentChange(item.id)}
              disabled={listLoading}
              className={`shrink-0 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                active
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-700'
              }`}
            >
              {item.label}{count !== null ? ` ${count}` : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ConversationRow({ thread, active, department, onSelect }) {
  const unread = (thread.unread_count || 0) > 0;
  const status = threadStatus(thread);
  const threadDept = threadDepartment(thread, department);
  const hasBark = threadHasBarkOpportunity(thread);

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(thread.id)}
        className={`w-full border-b border-slate-100 px-4 py-3 text-left transition-colors ${
          active
            ? 'bg-primary/5 shadow-[inset_3px_0_0_var(--color-primary,#0d4f3c)]'
            : 'bg-white hover:bg-slate-50'
        }`}
      >
        <div className="flex items-start gap-3">
          <span className={`mt-1 h-2.5 w-2.5 rounded-full ${unread ? 'bg-primary' : 'bg-slate-300'}`} aria-label={unread ? 'Unread' : 'Read'} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <p className={`truncate text-sm ${unread ? 'font-bold text-slate-950' : 'font-semibold text-slate-800'}`}>
                {threadDisplayName(thread)}
              </p>
              <span className="shrink-0 text-[11px] text-slate-500">{fmtWhen(thread.last_message_at || thread.updated_at)}</span>
            </div>
            <p className="mt-0.5 truncate text-sm font-medium text-slate-700">{thread.subject || 'No subject'}</p>
            <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{thread.last_preview || thread.preview || 'No preview available'}</p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Badge tone="blue">{departmentLabel(threadDept)}</Badge>
              <Badge tone={status === 'closed' ? 'slate' : 'green'}>{status === 'closed' ? 'Closed' : 'Open'}</Badge>
              {hasBark && <Badge tone="amber">External Bark lead</Badge>}
            </div>
          </div>
          <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
        </div>
      </button>
    </li>
  );
}

function ConversationList({
  threads,
  filteredThreads,
  selectedId,
  department,
  searchTerm,
  listLoading,
  fetchError,
  onSearchChange,
  onSelect,
  onRefresh,
  compactControls,
}) {
  return (
    <section className="flex flex-col rounded-lg border border-slate-200 bg-white shadow-sm md:h-full md:min-h-0 md:overflow-hidden" aria-label="Conversation list">
      {compactControls}
      <div className="shrink-0 border-b border-slate-200 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{departmentLabel(department)}</p>
            <h2 className="text-base font-semibold text-slate-950">
              {filteredThreads.length} conversation{filteredThreads.length === 1 ? '' : 's'}
            </h2>
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={listLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${listLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        <label className="mt-4 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus-within:border-primary focus-within:bg-white">
          <Search className="h-4 w-4 text-slate-400" />
          <span className="sr-only">Search conversations</span>
          <input
            value={searchTerm}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search sender, email, subject or preview"
            className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
          />
        </label>
      </div>

      <div className="md:min-h-0 md:flex-1 md:overflow-y-auto md:overscroll-contain" data-testid="conversation-results">
        {listLoading ? (
          <div className="flex min-h-[360px] flex-col items-center justify-center gap-3 text-slate-500 md:h-full md:min-h-0">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm font-medium">Loading conversations</p>
          </div>
        ) : fetchError ? (
          <div className="flex min-h-[360px] flex-col items-center justify-center gap-3 px-6 text-center text-slate-600 md:h-full md:min-h-0">
            <AlertCircle className="h-8 w-8 text-red-500" />
            <p className="font-semibold text-slate-900">Inbox could not be loaded</p>
            <p className="text-sm">{fetchError}</p>
            <Button variant="outline" size="sm" onClick={onRefresh}>Try again</Button>
          </div>
        ) : threads.length === 0 ? (
          <div className="flex min-h-[360px] flex-col items-center justify-center px-6 text-center text-slate-500 md:h-full md:min-h-0">
            <Mail className="mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm font-semibold text-slate-800">No conversations yet</p>
            <p className="mt-1 text-xs">Replies from the selected department will appear here automatically.</p>
          </div>
        ) : filteredThreads.length === 0 ? (
          <div className="flex min-h-[360px] flex-col items-center justify-center px-6 text-center text-slate-500 md:h-full md:min-h-0">
            <Search className="mb-3 h-9 w-9 text-slate-300" />
            <p className="text-sm font-semibold text-slate-800">No search results</p>
            <p className="mt-1 text-xs">Try a different sender, email, subject or preview term.</p>
          </div>
        ) : (
          <ul>
            {filteredThreads.map((thread) => (
              <ConversationRow
                key={thread.id}
                thread={thread}
                active={thread.id === selectedId}
                department={department}
                onSelect={onSelect}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function MessageCard({ message, deptMail }) {
  const outbound = message.direction === 'outbound';
  const barkOpportunityUrl = findBarkOpportunityUrl(message.body_text);

  return (
    <article className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
      <div className={`w-full max-w-[760px] rounded-lg border p-4 shadow-sm ${
        outbound
          ? 'border-primary/20 bg-primary/5'
          : 'border-slate-200 bg-white'
      }`}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
          <div>
            <p className="text-sm font-semibold text-slate-950">
              {outbound ? (deptMail?.send_from || 'Osabea') : (message.from_name || message.from_email || 'Sender')}
            </p>
            <p className="text-xs text-slate-500">
              {outbound ? `To ${message.to_email || message.recipient_email || 'recipient'}` : `From ${message.from_email || 'unknown sender'}`}
            </p>
          </div>
          <span className="flex items-center gap-1 text-xs text-slate-500">
            <Clock className="h-3.5 w-3.5" />
            {fmtWhen(message.created_at)}
          </span>
        </div>
        <MessageBody body={message.body_text} />
        {barkOpportunityUrl && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Badge tone="amber">External Bark lead</Badge>
            <Button variant="outline" size="sm" asChild>
              <a href={barkOpportunityUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Open opportunity on Bark
              </a>
            </Button>
          </div>
        )}
      </div>
    </article>
  );
}

function ReplyComposer({ deptMail, selectedThread, replyBody, sending, onChange, onSend }) {
  return (
    <div className="shrink-0 border-t border-slate-200 bg-white p-4" data-testid="reply-composer">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
        <span>
          From <strong>{deptMail?.send_from || departmentLabel(threadDepartment(selectedThread))}</strong>
        </span>
        <span>
          To <strong>{selectedThread?.contact_email || 'recipient'}</strong>
        </span>
      </div>
      <Textarea
        placeholder="Type your reply..."
        value={replyBody}
        onChange={(event) => onChange(event.target.value)}
        rows={5}
        className="min-h-[132px] resize-y rounded-lg border-slate-300 bg-slate-50 focus:bg-white"
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) onSend();
        }}
      />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500">Ctrl+Enter to send. Attachments and forwarding are not included in this phase.</p>
        <Button onClick={onSend} disabled={sending || !replyBody.trim()}>
          {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
          {sending ? 'Sending...' : 'Send reply'}
        </Button>
      </div>
    </div>
  );
}

function ReadingPane({
  selectedThread,
  messages,
  deptConfig,
  deptMail,
  detailLoading,
  detailError,
  canReply,
  replyBody,
  sending,
  onBack,
  onReplyChange,
  onSend,
  onRetry,
}) {
  if (!selectedThread) {
    return (
      <section className="hidden rounded-lg border border-slate-200 bg-white shadow-sm md:flex md:h-full md:min-h-0 md:flex-col md:items-center md:justify-center md:px-8 md:text-center">
        <Inbox className="mb-3 h-12 w-12 text-slate-300" />
        <p className="font-semibold text-slate-900">Select a conversation</p>
        <p className="mt-1 max-w-sm text-sm text-slate-500">Choose a thread from the list to review the message history and reply.</p>
      </section>
    );
  }

  const threadDept = threadDepartment(selectedThread, deptConfig.id);
  const status = threadStatus(selectedThread);
  const latest = selectedThread.last_message_at || selectedThread.updated_at;
  const hasBark = messages.some((message) => findBarkOpportunityUrl(message.body_text)) || threadHasBarkOpportunity(selectedThread);

  return (
    <section className="flex flex-col rounded-lg border border-slate-200 bg-slate-50 shadow-sm md:h-full md:min-h-0 md:overflow-hidden" aria-label="Reading pane">
      <div className="shrink-0 border-b border-slate-200 bg-white p-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="mb-3 md:hidden">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to inbox
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold leading-6 text-slate-950">{selectedThread.subject || 'No subject'}</h2>
            <p className="mt-1 text-sm text-slate-600">
              {threadDisplayName(selectedThread)}
              {selectedThread.contact_email && <span className="text-slate-500"> &lt;{selectedThread.contact_email}&gt;</span>}
            </p>
            <p className="mt-1 text-xs text-slate-500">To {deptMail?.reply_to || departmentLabel(threadDept)}</p>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to={deptConfig.crmPath(selectedThread)}>
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              {deptConfig.crmLabel}
            </Link>
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge tone="blue">{departmentLabel(threadDept)}</Badge>
          <Badge tone={status === 'closed' ? 'slate' : 'green'}>{status === 'closed' ? 'Closed' : 'Open'}</Badge>
          {hasBark && <Badge tone="amber">External Bark lead</Badge>}
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <Clock className="h-3.5 w-3.5" />
            {fmtWhen(latest)}
          </span>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4" data-testid="message-history">
        {detailLoading ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm font-medium">Loading conversation</p>
          </div>
        ) : detailError ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-center">
            <AlertCircle className="h-8 w-8 text-red-500" />
            <p className="font-semibold text-slate-900">Conversation could not be loaded</p>
            <p className="text-sm text-slate-500">{detailError}</p>
            <Button variant="outline" size="sm" onClick={onRetry}>Try again</Button>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-center text-slate-500">
            <Mail className="h-10 w-10 text-slate-300" />
            <p className="font-semibold text-slate-900">No messages in this thread</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <MessageCard key={message.id} message={message} deptMail={deptMail} />
            ))}
            <div id="portal-inbox-messages-end" />
          </div>
        )}
      </div>

      {canReply && (
        <ReplyComposer
          deptMail={deptMail}
          selectedThread={selectedThread}
          replyBody={replyBody}
          sending={sending}
          onChange={onReplyChange}
          onSend={onSend}
        />
      )}
    </section>
  );
}

export default function PortalInboxPage() {
  const { token, isManagerOrAdmin } = useAuth();
  const workspaceWindow = useWorkspaceWindow();
  const isWorkspaceWindow = Boolean(workspaceWindow);
  const managerAccess = isManagerOrAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const department = searchParams.get('department') || 'staffing';
  const threadParam = searchParams.get('thread');
  const contactParam = searchParams.get('contact');

  const deptConfig = departmentConfig(department);

  const [threads, setThreads] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [mailboxFilter, setMailboxFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [setup, setSetup] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fetchError, setFetchError] = useState('');
  const [detailError, setDetailError] = useState('');
  const contactResolvedRef = useRef(false);
  const detailRequestSeqRef = useRef(0);
  const activeDetailRequestRef = useRef(null);
  const listRequestSeqRef = useRef(0);
  const activeListDepartmentRef = useRef(null);
  const replyRequestSeqRef = useRef(0);
  const activeReplyThreadRef = useRef(null);
  const replySubmittingRef = useRef(false);

  const filteredThreads = useMemo(
    () => filterThreads(threads, { searchTerm, mailboxFilter, department }),
    [threads, searchTerm, mailboxFilter, department],
  );

  const invalidateDetailRequest = useCallback(() => {
    detailRequestSeqRef.current += 1;
    activeDetailRequestRef.current = null;
  }, []);

  const invalidateListRequest = useCallback(() => {
    listRequestSeqRef.current += 1;
    activeListDepartmentRef.current = null;
  }, []);

  const invalidateReplyContext = useCallback(() => {
    replyRequestSeqRef.current += 1;
    activeReplyThreadRef.current = null;
    replySubmittingRef.current = false;
  }, []);

  const resetConversationState = useCallback(({ loading = false, selectedThreadId = null } = {}) => {
    setSelectedId(selectedThreadId);
    setThreadDetail(null);
    setMessages([]);
    setReplyBody('');
    setDetailError('');
    setSending(false);
    setDetailLoading(loading);
  }, []);

  const setDepartment = (dept) => {
    invalidateDetailRequest();
    invalidateListRequest();
    invalidateReplyContext();
    const next = new URLSearchParams();
    next.set('department', dept);
    setSearchParams(next);
    resetConversationState();
    setSearchTerm('');
    contactResolvedRef.current = false;
    setListLoading(true);
  };

  const fetchThreads = useCallback(async ({ silent = false } = {}) => {
    const requestId = listRequestSeqRef.current + 1;
    const requestDepartment = department;
    listRequestSeqRef.current = requestId;
    activeListDepartmentRef.current = requestDepartment;

    if (!silent) setListLoading(true);
    setFetchError('');
    try {
      const res = await fetch(
        `${API}/portal-inbox/threads?department=${requestDepartment}&status=all`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (
        listRequestSeqRef.current !== requestId ||
        activeListDepartmentRef.current !== requestDepartment
      ) {
        return;
      }
      if (res.ok) {
        const data = await res.json();
        if (
          listRequestSeqRef.current !== requestId ||
          activeListDepartmentRef.current !== requestDepartment
        ) {
          return;
        }
        setThreads(data.threads || []);
      } else if (res.status === 403) {
        setFetchError('Manager or admin access required.');
        toast.error('Manager or admin access required');
      } else if (!silent) {
        setFetchError('The inbox service did not return conversations.');
        toast.error('Failed to load inbox');
      }
    } catch {
      if (
        listRequestSeqRef.current !== requestId ||
        activeListDepartmentRef.current !== requestDepartment
      ) {
        return;
      }
      if (!silent) {
        setFetchError('Check your connection and try again.');
        toast.error('Failed to load inbox');
      }
    } finally {
      if (
        listRequestSeqRef.current === requestId &&
        activeListDepartmentRef.current === requestDepartment
      ) {
        setListLoading(false);
      }
    }
  }, [token, department]);

  const fetchSetup = useCallback(async () => {
    try {
      const res = await fetch(`${API}/portal-inbox/setup`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSetup(await res.json());
    } catch {
      /* optional */
    }
  }, [token]);

  useEffect(() => () => {
    invalidateDetailRequest();
    invalidateListRequest();
    invalidateReplyContext();
  }, [invalidateDetailRequest, invalidateListRequest, invalidateReplyContext]);

  const loadThread = useCallback(async (threadId) => {
    if (!threadId) return;
    const knownThread = threads.find((thread) => thread.id === threadId);
    if (!knownThread || !threadBelongsToDepartment(knownThread, department)) {
      detailRequestSeqRef.current += 1;
      activeDetailRequestRef.current = threadId;
      setDetailLoading(false);
      setDetailError('This conversation is not available in the selected mailbox.');
      setThreadDetail(null);
      setMessages([]);
      setReplyBody('');
      return;
    }

    const requestId = detailRequestSeqRef.current + 1;
    detailRequestSeqRef.current = requestId;
    activeDetailRequestRef.current = threadId;

    setDetailLoading(true);
    setDetailError('');
    setThreadDetail(null);
    setMessages([]);
    setReplyBody('');
    try {
      const res = await fetch(`${API}/portal-inbox/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (detailRequestSeqRef.current !== requestId || activeDetailRequestRef.current !== threadId) return;
      if (res.ok) {
        const data = await res.json();
        if (detailRequestSeqRef.current !== requestId || activeDetailRequestRef.current !== threadId) return;
        const returnedThread = data?.thread;
        if (
          !returnedThread ||
          returnedThread.id !== threadId ||
          !threadBelongsToDepartment(returnedThread, department)
        ) {
          setDetailError('This conversation is not available in the selected mailbox.');
          setThreadDetail(null);
          setMessages([]);
          setReplyBody('');
          return;
        }
        setThreadDetail(data.thread);
        setMessages(data.messages || []);
        setSelectedId(threadId);
        setThreads((prev) =>
          prev.map((t) => (t.id === threadId ? { ...t, unread_count: 0 } : t))
        );
        fetchThreads({ silent: true });
      } else {
        setDetailError('Thread not found.');
        toast.error('Thread not found');
      }
    } catch {
      if (detailRequestSeqRef.current !== requestId || activeDetailRequestRef.current !== threadId) return;
      setDetailError('Failed to load this thread.');
      toast.error('Failed to load thread');
    } finally {
      if (detailRequestSeqRef.current === requestId && activeDetailRequestRef.current === threadId) {
        setDetailLoading(false);
      }
    }
  }, [token, fetchThreads, threads, department]);

  useEffect(() => {
    if (!managerAccess) return;
    invalidateDetailRequest();
    invalidateListRequest();
    invalidateReplyContext();
    contactResolvedRef.current = false;
    setListLoading(true);
    fetchThreads();
    fetchSetup();
  }, [department, fetchThreads, fetchSetup, managerAccess, invalidateDetailRequest, invalidateListRequest, invalidateReplyContext]);

  useEffect(() => {
    if (!threadParam || listLoading) return;
    if (threadDetail?.id === threadParam && selectedId === threadParam && !detailError) return;
    if (activeDetailRequestRef.current === threadParam) return;
    if (selectedId !== threadParam) {
      resetConversationState({ loading: true, selectedThreadId: threadParam });
    }
    loadThread(threadParam);
  }, [threadParam, listLoading, threadDetail?.id, selectedId, detailError, detailLoading, loadThread, resetConversationState]);

  useEffect(() => {
    if (!contactParam || listLoading || threadParam || contactResolvedRef.current) return;
    const norm = contactParam.trim().toLowerCase();
    const match = threads.find((t) => (t.contact_email || '').toLowerCase() === norm);
    if (match) {
      contactResolvedRef.current = true;
      const next = new URLSearchParams(searchParams);
      next.set('thread', match.id);
      setSearchParams(next, { replace: true });
    }
  }, [contactParam, listLoading, threadParam, threads, searchParams, setSearchParams]);

  const selectThread = (threadId) => {
    if (threadId === selectedId && threadDetail?.id === threadId) return;
    invalidateDetailRequest();
    invalidateReplyContext();
    resetConversationState({ loading: true, selectedThreadId: threadId });
    const next = new URLSearchParams(searchParams);
    next.set('thread', threadId);
    setSearchParams(next);
  };

  const closeReadingPane = () => {
    invalidateDetailRequest();
    invalidateReplyContext();
    const next = new URLSearchParams(searchParams);
    next.delete('thread');
    setSearchParams(next);
    resetConversationState();
  };

  const retrySelectedThread = () => {
    if (!selectedId) return;
    loadThread(selectedId);
  };

  const sendReply = async () => {
    const body = replyBody.trim();
    const replyThreadId = threadDetail?.id;
    if (
      !body ||
      !threadDetail ||
      threadDetail.id !== selectedId ||
      detailLoading ||
      detailError ||
      sending ||
      replySubmittingRef.current
    ) {
      return;
    }
    const requestId = replyRequestSeqRef.current + 1;
    replyRequestSeqRef.current = requestId;
    activeReplyThreadRef.current = replyThreadId;
    replySubmittingRef.current = true;
    setSending(true);
    try {
      const res = await fetch(`${API}/portal-inbox/threads/${replyThreadId}/reply`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ body }),
      });
      const replyStillCurrent = () =>
        replyRequestSeqRef.current === requestId &&
        activeReplyThreadRef.current === replyThreadId &&
        threadDetail?.id === replyThreadId &&
        selectedId === replyThreadId &&
        !detailLoading &&
        !detailError;
      if (res.ok) {
        const data = await res.json();
        if (!replyStillCurrent()) return;
        setMessages((prev) => [...prev, data.message]);
        setReplyBody('');
        toast.success('Reply sent');
        fetchThreads({ silent: true });
      } else {
        if (!replyStillCurrent()) return;
        const err = await res.json().catch(() => ({}));
        if (!replyStillCurrent()) return;
        toast.error(err.detail || 'Failed to send reply');
      }
    } catch {
      if (
        replyRequestSeqRef.current !== requestId ||
        activeReplyThreadRef.current !== replyThreadId ||
        threadDetail?.id !== replyThreadId ||
        selectedId !== replyThreadId
      ) {
        return;
      }
      toast.error('Failed to send reply');
    } finally {
      if (
        replyRequestSeqRef.current === requestId &&
        activeReplyThreadRef.current === replyThreadId &&
        threadDetail?.id === replyThreadId &&
        selectedId === replyThreadId
      ) {
        replySubmittingRef.current = false;
        setSending(false);
      }
    }
  };

  if (!managerAccess) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
        Portal Inbox is restricted to managers and administrators.
      </div>
    );
  }

  const selectedThread = threadDetail || threads.find((t) => t.id === selectedId) || (
    selectedId && (detailLoading || detailError)
      ? { id: selectedId, subject: 'Conversation unavailable', department }
      : null
  );
  const canReply = Boolean(
    threadDetail &&
    threadDetail.id === selectedId &&
    !detailLoading &&
    !detailError,
  );
  const deptMail = setup?.departments?.find((d) => d.id === department);
  const showMobileList = !selectedThread;
  const pageShellClass = isWorkspaceWindow
    ? 'min-h-0 space-y-4 md:flex md:h-full md:min-h-0 md:flex-col md:overflow-hidden'
    : 'min-h-[calc(100vh-140px)] space-y-4 md:flex md:h-[calc(100vh-140px)] md:min-h-0 md:flex-col md:overflow-hidden';

  return (
    <div className={pageShellClass} data-testid="portal-inbox-page">
      <div className="shrink-0 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">Portal Inbox</p>
          <h1 className="text-xl font-semibold text-slate-950">Conversations</h1>
          <p className="mt-1 text-sm text-slate-500">Staffing, recruitment and general replies in one workspace inbox.</p>
        </div>
        <div className="flex items-center gap-2">
          {countUnread(threads) > 0 && <Badge tone="red">{countUnread(threads)} unread</Badge>}
          <Badge tone="green"><CheckCircle2 className="mr-1 h-3 w-3" /> Manager access</Badge>
        </div>
      </div>

      {showSetup && setup && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm shadow-sm">
          <p className="font-semibold text-amber-900">Inbox configuration - {setup.cost}</p>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            {(setup.departments || []).map((d) => (
              <div key={d.id} className="rounded-md border border-amber-100 bg-white/80 p-3">
                <p className="font-medium text-amber-900">{d.label}</p>
                <p className="mt-1 text-xs text-amber-800">Send: {d.send_from}</p>
                <p className="text-xs text-amber-800">Replies: {d.reply_to}</p>
              </div>
            ))}
          </div>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-amber-800">
            {(setup.steps || []).map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={`grid gap-4 md:min-h-0 md:flex-1 md:overflow-hidden md:grid-cols-[minmax(280px,380px)_minmax(0,1fr)] lg:grid-cols-[220px_minmax(300px,380px)_minmax(0,1fr)] ${showMobileList ? '' : ''}`} data-testid="portal-inbox-grid">
        <div className="hidden lg:block lg:h-full lg:min-h-0" data-testid="mailbox-column">
          <MailboxSidebar
            department={department}
            mailboxFilter={mailboxFilter}
            threads={threads}
            listLoading={listLoading}
            onDepartmentChange={setDepartment}
            onMailboxFilterChange={setMailboxFilter}
            onRefresh={() => fetchThreads()}
            onSetup={() => setShowSetup((value) => !value)}
          />
        </div>

        <div className={`${showMobileList ? 'block' : 'hidden md:block'} md:h-full md:min-h-0`} data-testid="conversation-column">
          <ConversationList
            threads={threads}
            filteredThreads={filteredThreads}
            selectedId={selectedId}
            department={department}
            searchTerm={searchTerm}
            listLoading={listLoading}
            fetchError={fetchError}
            onSearchChange={setSearchTerm}
            onSelect={selectThread}
            onRefresh={() => fetchThreads()}
            compactControls={(
              <CompactInboxControls
                department={department}
                mailboxFilter={mailboxFilter}
                threads={threads}
                listLoading={listLoading}
                onDepartmentChange={setDepartment}
                onMailboxFilterChange={setMailboxFilter}
              />
            )}
          />
        </div>

        <div className={`${showMobileList ? 'hidden md:block' : 'block'} md:h-full md:min-h-0`} data-testid="reading-column">
          <ReadingPane
            selectedThread={selectedThread}
            messages={messages}
            deptConfig={deptConfig}
            deptMail={deptMail}
            detailLoading={detailLoading}
            detailError={detailError}
            canReply={canReply}
            replyBody={replyBody}
            sending={sending}
            onBack={closeReadingPane}
            onReplyChange={setReplyBody}
            onSend={sendReply}
            onRetry={retrySelectedThread}
          />
        </div>
      </div>
    </div>
  );
}
